import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteWork4MeSkill, getWork4MeSkill, updateWork4MeSkill } from '@/api/skills-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { toast } from 'sonner';
import { Work4MeSkillForm } from './Work4MeSkillForm';

export function Work4MeSkillDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const parsedSkillId = Number(id);
  const isValidSkillId = Number.isInteger(parsedSkillId) && parsedSkillId > 0;

  const skillQuery = useQuery({
    queryKey: ['work4me-skill', parsedSkillId],
    queryFn: () => getWork4MeSkill(parsedSkillId),
    enabled: isValidSkillId,
  });

  const skill = skillQuery.data;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [body, setBody] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);

  useEffect(() => {
    if (!skill) return;
    setName(skill.name);
    setDescription(skill.description ?? '');
    setBody(skill.body);
    setIsEnabled(skill.isEnabled);
  }, [skill]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!isValidSkillId) throw new Error('Invalid skill id');
      return updateWork4MeSkill(parsedSkillId, {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        body: body.trim(),
        isEnabled,
      });
    },
    onSuccess: async (updated) => {
      toast.success(`Saved "${updated.name}".`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['work4me-skill', parsedSkillId] }),
        queryClient.invalidateQueries({ queryKey: ['process-skill-options'] }),
      ]);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to save skill.';
      toast.error(message);
    },
  });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!isValidSkillId) throw new Error('Invalid skill id');
      return deleteWork4MeSkill(parsedSkillId);
    },
    onSuccess: async () => {
      toast.success('Skill deleted.');
      await queryClient.invalidateQueries({ queryKey: ['process-skill-options'] });
      navigate('/work4me/skills');
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to delete skill.'));
    },
  });

  const isDirty =
    Boolean(skill)
    && (
      name.trim() !== (skill?.name ?? '')
      || (description.trim() ? description.trim() : '') !== (skill?.description ?? '')
      || body.trim() !== (skill?.body ?? '')
      || isEnabled !== skill?.isEnabled
    );

  if (!isValidSkillId) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Invalid skill id.</p>
      </div>
    );
  }

  if (skillQuery.isLoading) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-muted-foreground">Loading skill...</p>
      </div>
    );
  }

  if (skillQuery.isError || !skill) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Unable to load skill detail.</p>
      </div>
    );
  }

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Skill: {skill.name}</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge variant="outline">{skill.ownerScope}</Badge>
              <Badge variant={isEnabled ? 'default' : 'secondary'}>
                {isEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/work4me/skills')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Skills
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete skill</DialogTitle>
            <DialogDescription>
              Delete '{skill.name}'? It will be removed from any agents using it.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Work4MeSkillForm
        name={name}
        description={description}
        body={body}
        isEnabled={isEnabled}
        isDirty={isDirty}
        isSaving={saveMutation.isPending}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onBodyChange={setBody}
        onIsEnabledChange={setIsEnabled}
        onSave={() => saveMutation.mutate()}
      />
    </div>
  );
}
